import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const FeaturedContent = () => {
  const navigate = useNavigate();
  const featuredArticle = {
    id: 7,
    title: "Buying AI Tools Won\'t Make You AI-Ready: From Data Warehouse to AI Platform",
    excerpt: `The transition from traditional data warehousing to AI-enabled platforms is no longer optional for mid-sized organisations. Competitive pressure, regulatory scrutiny, and rising executive expectations are accelerating the demand for predictive and prescriptive analytics.\n\nHowever, many organisations make a critical mistake: they attempt to "upgrade" tools rather than redesign their architecture. The result is a fragmented pseudo-AI environment layered on top of legacy foundations that were never built for scalable intelligence.\n\nThis comprehensive guide explores four recurring architectural failures that undermine AI platform transformation: lakehouse misconceptions, governance gaps, metadata neglect, and tool-centric rather than platform-centric thinking.`,
    author: "Northstar Analytics",
    authorRole: "Data Strategy Experts",
    authorImage: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
    publishDate: "February 15, 2026",
    readTime: "18 min read",
    category: "AI Readiness",
    image: "https://images.unsplash.com/photo-1639322537228-f710d846310a?w=800&h=400&fit=crop",
    tags: ["AI Readiness", "Data Architecture", "Platform Thinking", "Governance"],
    featured: true
  };

  const trendingTopics = [
    {
      id: 1,
      title: "GDPR Compliance in 2024",
      count: "15 resources",
      icon: "Shield",
      color: "text-blue-600"
    },
    {
      id: 2,
      title: "Data Mesh Architecture",
      count: "12 resources",
      icon: "Network",
      color: "text-green-600"
    },
    {
      id: 3,
      title: "Real-time Analytics",
      count: "18 resources",
      icon: "Zap",
      color: "text-purple-600"
    },
    {
      id: 4,
      title: "Data Quality Management",
      count: "22 resources",
      icon: "CheckCircle",
      color: "text-orange-600"
    }
  ];

  return (
    <section className="py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-section-title text-primary mb-4">Featured Content</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Stay ahead with our latest insights and most popular resources
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Featured Article */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-elevation overflow-hidden hover:shadow-elevation-2 hover:scale-[1.02] transition-all duration-300">
              <div className="relative">
                <Image
                  src={featuredArticle?.image}
                  alt={featuredArticle?.title}
                  className="w-full h-64 lg:h-80 object-cover"
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-secondary text-white px-3 py-1 rounded-full text-sm font-medium">
                    Featured
                  </span>
                </div>
                <div className="absolute top-4 right-4">
                  <span className="bg-white/90 backdrop-blur-sm text-primary px-3 py-1 rounded-full text-sm font-medium">
                    {featuredArticle?.category}
                  </span>
                </div>
              </div>

              <div className="p-6 lg:p-8">
                <div className="flex items-center space-x-4 mb-4">
                  <Image
                    src={featuredArticle?.authorImage}
                    alt={featuredArticle?.author}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <div className="font-medium text-primary">{featuredArticle?.author}</div>
                    <div className="text-sm text-muted-foreground">{featuredArticle?.authorRole}</div>
                  </div>
                </div>

                <h3 className="text-xl lg:text-2xl font-bold text-primary mb-4 leading-tight">
                  {featuredArticle?.title}
                </h3>

                <p className="text-muted-foreground mb-6 leading-relaxed">
                  {featuredArticle?.excerpt?.split('\n\n')?.[0]}
                </p>

                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                    <div className="flex items-center space-x-1">
                      <Icon name="Calendar" size={16} />
                      <span>{featuredArticle?.publishDate}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="Clock" size={16} />
                      <span>{featuredArticle?.readTime}</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {featuredArticle?.tags?.map((tag, index) => (
                    <span
                      key={index}
                      className="bg-surface text-primary px-3 py-1 rounded-full text-sm font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <Button
                  variant="default"
                  iconName="ArrowRight"
                  iconPosition="right"
                  className="w-full lg:w-auto hover:scale-105 active:scale-95 transition-all duration-200"
                  onClick={() => navigate('/article/ai-readiness')}
                >
                  Read Full Article
                </Button>
              </div>
            </div>
          </div>

          {/* Trending Topics Sidebar */}
          <div className="space-y-6">
            <div className="bg-surface rounded-xl p-6">
              <h3 className="text-lg font-semibold text-primary mb-4 flex items-center space-x-2">
                <Icon name="TrendingUp" size={20} />
                <span>Trending Topics</span>
              </h3>
              <div className="space-y-4">
                {trendingTopics?.map((topic) => (
                  <div
                    key={topic?.id}
                    className="flex items-center space-x-3 p-3 bg-white rounded-lg hover:shadow-md hover:scale-[1.02] active:scale-95 transition-all duration-300 cursor-pointer"
                  >
                    <div className={`p-2 rounded-lg bg-gray-100 ${topic?.color}`}>
                      <Icon name={topic?.icon} size={16} />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-primary text-sm">{topic?.title}</div>
                      <div className="text-xs text-muted-foreground">{topic?.count}</div>
                    </div>
                    <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
                  </div>
                ))}
              </div>
            </div>

            {/* Newsletter Signup */}
            <div className="bg-gradient-brand rounded-xl p-6 text-white">
              <div className="text-center">
                <Icon name="Mail" size={32} className="mx-auto mb-4 text-secondary" />
                <h3 className="text-lg font-semibold mb-2">Weekly Data Insights</h3>
                <p className="text-white/90 text-sm mb-4">
                  Get the latest data strategy insights delivered to your inbox every Tuesday.
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  iconName="ArrowRight"
                  iconPosition="right"
                  className="w-full hover:scale-105 active:scale-95 transition-all duration-200"
                >
                  Subscribe Now
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedContent;